import React, { useState, useEffect, useContext, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { InputTextarea } from "primereact/inputtextarea";
import { InputNumber } from "primereact/inputnumber";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { Tooltip } from "primereact/tooltip";
import { Accordion, AccordionTab } from "primereact/accordion";
import { AuthContext } from "../../../Header/auth - context";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import usePermission from "../../../custom/usePermission";
import CustomDropdown from "../../../custom/customDropdown";

const TIP_DOKUMENTA_OPTIONS = [
  { value: 381, label: "KO – Dokument o smanjenju (Knjižno odobrenje)" },
  { value: 383, label: "KZ – Dokument o povećanju (Knjižno zaduženje)" },
];

const SEF_POSLAT = 219;
const SEF_NIJE_POSLAT = 218;

const emptyForm = {
  brojDokumenta: "",
  id_klijent: null,
  TipDokumenta: null,
  id_tip_dokumenta: null,
  id_podtip_dokumenta: null,
  statusProvere: null,
  user_id_proverio: null,
  napomenaProvera: "",
  vozilo: "",
  cmr: "",
  refNasa: "",
  refNo: "",
  iznos: null,
  iznosValuta: "",
  napomenaPoreskoOslobadjanje: "",
  napomena: "",
  datum: null,
  mestoIzdavanja: "",
  dpu: null,
  rokPlacanjaDani: null,
  rokPlacanjaDatum: null,
  tekuciRacun: null,
  valuta: null,
  kurs: null,
  statusSefa: null,
  sefStatusString: null,
  sefKomentar: null,
  datumSlanjaPoste: null,
  datumPrijemaPoste: null,
  DateEntered: null,
};

const emptyItem = {
  sifraStavke: null,
  tekst: "",
  slobodanTekst: "",
  jedinicnaCena: null,
  kolicina: null,
  neto: null,
  bruto: null,
  PDVstopa: null,
  PDVIznos: null,
  id_vozilo: null,
  id_magacin: null,
  artikal: null,
  jedinicaMere: null,
  rabatProc: null,
  rabat: null,
};

const NovaDokumentKOKZ = (props) => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const toast = useRef(null);
  const company = useSelector((s) => s.company);
  const userRole = useSelector((s) => s.user_role);
  const eskaliran = useSelector((s) => s.eskaliran);
  const { can } = usePermission();

  const {
    isDinarska,
    klijentiOptions = [],
    valutaOptions = [],
    kurseviOptions = [],
    tekuciRacunOptions: tekuciRacunAll = [],
    sifraStavkeOptions = [],
    jedinicaMereOptions = [],
    statusSefaOptions = [],
    vozilaOptions = [],
    idDokument,
    setIdDokument,
  } = props;

  const [formData, setFormData] = useState(emptyForm);
  const [formDataItems, setFormDataItems] = useState(emptyItem);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formSubmittedItems, setFormSubmittedItems] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [globalFilter, setGlobalFilter] = useState(null);
  const [sefKonfigurisan, setSefKonfigurisan] = useState(false);
  const [sefRazlog, setSefRazlog] = useState('');
  const [sefLoading, setSefLoading] = useState(false);
  const [sefDialog, setSefDialog] = useState({ visible: false, type: null, komentar: "" });
  const [faktureOptions, setFaktureOptions] = useState([]);
  const [selectedFakture, setSelectedFakture] = useState([]);
  const [prevuciEnabled, setPrevuciEnabled] = useState(false);
  const [prevuciLoading, setPrevuciLoading] = useState(false);
  const [stavkaDialogVisible, setStavkaDialogVisible] = useState(false);

  // Sifarnici iz INIT
  const [tipDokumentaKOKZOptions, setTipDokumentaKOKZOptions] = useState([]);

  const tekuciRacunOptions = (tekuciRacunAll || []).filter(
    (x) => x?.tipRacuna === (isDinarska ? 1 : 2)
  );

  const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + "Z";
  };

  const addDays = (date, days) => {
    if (!date || days == null) return null;
    const d = new Date(date);
    d.setDate(d.getDate() + Number(days));
    return d;
  };

  const handleErrors = (res) => {
    if (res.ok) return res.json();
    throw res;
  };

  /* ── Lock logika ── */
  const isSefPoslat =
    formData.statusSefa === SEF_POSLAT &&
    !["Storno", "Cancelled"].includes(formData.sefStatusString);
  const isSefStorniran = ["Storno", "Cancelled"].includes(formData.sefStatusString);
  const isLocked = isSefPoslat || isSefStorniran;
  const lockReason = isSefStorniran
    ? "Dokument je storniran/otkazan na SEF-u — forma je zaključana."
    : isSefPoslat
    ? "Dokument je poslat na SEF — izmene nisu dozvoljene."
    : "";
  const lockStyle = isSefStorniran
    ? { background: "#fdecea", border: "1px solid #f5c2c0", color: "#a02622" }
    : isSefPoslat
    ? { background: "#fff8e1", border: "1px solid #ffe69c", color: "#856404" }
    : null;

  /* ── SEF konfiguracija ── */
  useEffect(() => {
    fetch(process.env.REACT_APP_BACKEND_URL + "/sef/CheckConfig", {
      headers: { Authorization: "Bearer " + auth.token },
    })
      .then((r) => r.json())
      .then((d) => {
        setSefKonfigurisan(d.configured === true);
        setSefRazlog(d.reason || "");
      })
      .catch(() => { setSefKonfigurisan(false); setSefRazlog(""); });
  }, []);

  /* ── INIT: sifarnici ── */
  useEffect(() => {
    fetch(process.env.REACT_APP_BACKEND_URL + "/invoice/KoKzInit", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
      body: JSON.stringify({}),
    })
      .then(handleErrors)
      .then((data) => {
        setTipDokumentaKOKZOptions(data.TipDokumentaKOKZDB || []);
      })
      .catch(() => {});
  }, []);

  /* ── Fakture za multiselect — puni se tek kad je klijent izabran ── */
  useEffect(() => {
    if (!formData.id_klijent) {
      setFaktureOptions([]);
      return;
    }
    const podTipFakture = isDinarska ? 305 : 306;
    fetch(process.env.REACT_APP_BACKEND_URL + "/invoice/KoKzGetFakture", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
      body: JSON.stringify({ podTipFakture, id_klijent: formData.id_klijent }),
    })
      .then(handleErrors)
      .then((data) => setFaktureOptions(data || []))
      .catch(() => setFaktureOptions([]));
  }, [isDinarska, formData.id_klijent]);

  /* ── Učitaj dokument ── */
  useEffect(() => {
    if (idDokument) {
      loadDokument(idDokument);
    } else {
      resetAll();
    }
  }, [idDokument]);

  const loadDokument = (id) => {
    fetch(process.env.REACT_APP_BACKEND_URL + "/invoice/KoKzGetById", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
      body: JSON.stringify({ id }),
    })
      .then(handleErrors)
      .then((data) => {
        const h = data.header;
        setFormData({
          ...emptyForm,
          ...h,
          datum: h.datum ? new Date(h.datum) : null,
          dpu: h.dpu ? new Date(h.dpu) : null,
          rokPlacanjaDatum: h.rokPlacanjaDatum ? new Date(h.rokPlacanjaDatum) : null,
          datumSlanjaPoste: h.datumSlanjaPoste ? new Date(h.datumSlanjaPoste) : null,
          datumPrijemaPoste: h.datumPrijemaPoste ? new Date(h.datumPrijemaPoste) : null,
          statusSefa: h.statusSEF,
          sefStatusString: h.sefStatus,
          sefKomentar: h.sefKomentar,
        });
        setTableData(data.stavke || []);
        const faktIds = (data.fakture || []).map((f) => f.value);
        setSelectedFakture(faktIds);
        setPrevuciEnabled(faktIds.length > 0 && (data.stavke || []).length === 0);
      })
      .catch((err) => {
        err.json?.().then((e) =>
          toast.current.show({ severity: "error", summary: "Greška", detail: e.message, life: 4000 })
        );
      });
  };

  const resetAll = () => {
    setFormData(emptyForm);
    setFormDataItems(emptyItem);
    setTableData([]);
    setSelectedItemId(null);
    setSelectedFakture([]);
    setPrevuciEnabled(false);
    setFormSubmitted(false);
    setFormSubmittedItems(false);
    setActiveIndex(0);
  };

  /* ── Handlers ── */
  const handleChange = (e) => {
    const { name, value } = e.target || e;
    if (name === "id_klijent") {
      setSelectedFakture([]);
    }
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "rokPlacanjaDani") {
        next.rokPlacanjaDatum = addDays(prev.datum || new Date(), value);
      }
      return next;
    });
  };

  const handleChangeItems = (e) => {
    const { name, value } = e.target || e;
    setFormDataItems((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "sifraStavke") {
        const sifra = sifraStavkeOptions.find((x) => x.value === value);
        if (sifra) {
          next.tekst = sifra.Opis || "";
          next.PDVstopa = sifra.PDVStopa;
          next.slobodanTekst = sifra.TekstOslobodjenja || "";
        }
      }
      if (
        (name === "neto" || name === "PDVstopa") &&
        next.neto != null &&
        next.PDVstopa != null
      ) {
        next.PDVIznos = (next.neto * next.PDVstopa) / 100;
        next.bruto = next.neto + next.PDVIznos;
      }
      if (name === "kolicina" && prev.jedinicnaCena) {
        next.neto = (value || 0) * (prev.jedinicnaCena || 0);
      }
      if (name === "jedinicnaCena" && prev.kolicina) {
        next.neto = (prev.kolicina || 0) * (value || 0);
      }
      return next;
    });
  };

  const invalidCtrl = (v) => formSubmitted && (v == null || v === "");
  const invalidCtrlItem = (v) => formSubmittedItems && (v == null || v === "");

  /* ── Izlazni tip (iz INIT sifarnika) ── */
  const getIdTipIzlazni = () => {
    const found = tipDokumentaKOKZOptions.find(
      (x) => x.label?.toLowerCase().includes("izlazni")
    );
    return found?.value || null;
  };

  /* ── Save Header ── */
  const handleSave = () => {
    if (isLocked) {
      toast.current.show({ severity: "warn", summary: lockReason, life: 3000 });
      return;
    }
    setFormSubmitted(true);
    if (!formData.id_klijent || !formData.TipDokumenta || !formData.valuta || !formData.datum) {
      toast.current.show({
        severity: "warn",
        summary: "Upozorenje",
        detail: "Popunite obavezna polja (Klijent, Tip dokumenta, Valuta, Datum).",
        life: 3000,
      });
      return;
    }

    const payload = {
      id: idDokument || 0,
      id_klijent: formData.id_klijent,
      TipDokumenta: formData.TipDokumenta,
      id_tip_dokumenta: formData.id_tip_dokumenta || getIdTipIzlazni(),
      id_podtip_dokumenta: isDinarska ? 305 : 306,
      vozilo: formData.vozilo,
      cmr: formData.cmr,
      refNasa: formData.refNasa,
      refNo: formData.refNo,
      iznos: formData.iznos,
      napomenaPoreskoOslobadjanje: formData.napomenaPoreskoOslobadjanje,
      napomena: formData.napomena,
      datum: toUtcIso(formData.datum),
      mestoIzdavanja: company?.companyCity || formData.mestoIzdavanja,
      dpu: toUtcIso(formData.dpu),
      rokPlacanjaDani: formData.rokPlacanjaDani,
      rokPlacanjaDatum: toUtcIso(formData.rokPlacanjaDatum),
      tekuciRacun: formData.tekuciRacun,
      valuta: formData.valuta,
      kurs: formData.kurs,
      statusSefa: formData.statusSefa,
      datumSlanjaPoste: toUtcIso(formData.datumSlanjaPoste),
      datumPrijemaPoste: toUtcIso(formData.datumPrijemaPoste),
      fakture: selectedFakture,
    };

    fetch(process.env.REACT_APP_BACKEND_URL + "/invoice/KoKzSaveHeader", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
      body: JSON.stringify(payload),
    })
      .then(handleErrors)
      .then((data) => {
        if (!idDokument && data.ID) {
          setIdDokument(data.ID);
          if (props.setIdDokument) props.setIdDokument(data.ID);
        }
        if (data.brojDokumenta) {
          setFormData((prev) => ({ ...prev, brojDokumenta: data.brojDokumenta }));
        }
        if (selectedFakture.length > 0 && tableData.length === 0) {
          setPrevuciEnabled(true);
        }
        setFormSubmitted(false);
        toast.current.show({ severity: "success", summary: data.message, life: 3000 });
      })
      .catch((err) => {
        err.json?.().then((e) =>
          toast.current.show({ severity: "error", summary: "Greška", detail: e.message, life: 4000 })
        );
      });
  };

  /* ── Prevuci stavke ── */
  const handlePrevuciStavke = () => {
    if (!idDokument) return;
    setPrevuciLoading(true);
    fetch(process.env.REACT_APP_BACKEND_URL + "/invoice/KoKzPrevuciStavke", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
      body: JSON.stringify({ id: idDokument }),
    })
      .then(handleErrors)
      .then((data) => {
        setTableData(data || []);
        setPrevuciEnabled(false);
        toast.current.show({ severity: "success", summary: "Stavke preuzete sa faktura!", life: 3000 });
      })
      .catch((err) => {
        err.json?.().then((e) =>
          toast.current.show({ severity: "error", summary: "Greška", detail: e.message, life: 4000 })
        );
      })
      .finally(() => setPrevuciLoading(false));
  };

  /* ── Save Item ── */
  const handleSaveItem = () => {
    if (!idDokument) {
      toast.current.show({ severity: "warn", summary: "Sačuvajte dokument pre dodavanja stavki.", life: 3000 });
      return;
    }
    setFormSubmittedItems(true);
    if (!formDataItems.sifraStavke || !formDataItems.neto) {
      toast.current.show({
        severity: "warn",
        summary: "Upozorenje",
        detail: "Popunite obavezna polja stavke (Šifra, Neto).",
        life: 3000,
      });
      return;
    }

    fetch(process.env.REACT_APP_BACKEND_URL + "/invoice/KoKzSaveItem", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
      body: JSON.stringify({ id: idDokument, id_stavke: selectedItemId || 0, ...formDataItems }),
    })
      .then(handleErrors)
      .then((data) => {
        if (selectedItemId) {
          setTableData((prev) =>
            prev.map((r) => (r.id === selectedItemId ? { ...r, ...formDataItems } : r))
          );
        } else {
          setTableData((prev) => [...prev, { id: data.ID, ...formDataItems }]);
        }
        setFormDataItems(emptyItem);
        setSelectedItemId(null);
        setFormSubmittedItems(false);
        setStavkaDialogVisible(false);
        toast.current.show({ severity: "success", summary: data.message, life: 3000 });
      })
      .catch((err) => {
        err.json?.().then((e) =>
          toast.current.show({ severity: "error", summary: "Greška", detail: e.message, life: 4000 })
        );
      });
  };

  /* ── Row select stavke → otvori Dialog ── */
  const handleRowSelect = (row) => {
    setSelectedItemId(row.id);
    setFormDataItems({
      sifraStavke: row.sifraStavke,
      tekst: row.tekst || "",
      slobodanTekst: row.slobodanTekst || "",
      jedinicnaCena: row.jedinicnaCena,
      kolicina: row.kolicina,
      neto: row.neto,
      bruto: row.bruto,
      PDVstopa: row.PDVstopa,
      PDVIznos: row.PDVIznos,
      id_vozilo: row.id_vozilo,
      id_magacin: row.id_magacin,
      artikal: row.artikal,
      jedinicaMere: row.jedinicaMere,
      rabatProc: row.rabatProc,
      rabat: row.rabat,
    });
    setStavkaDialogVisible(true);
  };

  const openNewStavka = () => {
    setSelectedItemId(null);
    setFormDataItems(emptyItem);
    setFormSubmittedItems(false);
    setStavkaDialogVisible(true);
  };

  const closeStavkaDialog = () => {
    setStavkaDialogVisible(false);
    setFormSubmittedItems(false);
  };

  /* ── Delete Item ── */
  const handleDeleteItem = () => {
    if (!selectedItemId) return;
    confirmDialog({
      message: "Obrisati izabranu stavku?",
      acceptLabel: "Da",
      rejectLabel: "Ne",
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      accept: () => {
        fetch(process.env.REACT_APP_BACKEND_URL + "/invoice/KoKzDeleteItem", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
          body: JSON.stringify({ id: idDokument, id_stavke: selectedItemId }),
        })
          .then(handleErrors)
          .then(() => {
            setTableData((prev) => prev.filter((r) => r.id !== selectedItemId));
            setSelectedItemId(null);
            setFormDataItems(emptyItem);
            setStavkaDialogVisible(false);
            toast.current.show({ severity: "success", summary: "Stavka obrisana!", life: 3000 });
          })
          .catch(() =>
            toast.current.show({ severity: "error", summary: "Greška pri brisanju stavke.", life: 3000 })
          );
      },
    });
  };

  /* ── Delete Dokument ── */
  const handleDelete = () => {
    if (!idDokument) return;
    if (!can('FAKTURE', 'delete')) {
      toast.current.show({ severity: "warn", summary: "Nemate ovlašćenje za brisanje.", life: 3000 });
      return;
    }
    confirmDialog({
      message: "Obrisati ovaj dokument?",
      acceptLabel: "Da",
      rejectLabel: "Ne",
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      accept: () => {
        fetch(process.env.REACT_APP_BACKEND_URL + "/invoice/KoKzDelete", {
          method: "DELETE",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
          body: JSON.stringify({ id: idDokument }),
        })
          .then(handleErrors)
          .then(() => {
            resetAll();
            if (props.setIdDokument) props.setIdDokument(null);
            toast.current.show({ severity: "success", summary: "Dokument obrisan!", life: 3000 });
          })
          .catch(() =>
            toast.current.show({ severity: "error", summary: "Greška pri brisanju.", life: 3000 })
          );
      },
    });
  };

  /* ── SEF: Pošalji ── */
  const handleSendToSef = () => {
    if (!sefKonfigurisan) {
      toast.current.show({ severity: "error", summary: t('sef.nijeKonfigurisan'),
        detail: sefRazlog || t('sef.nemaKljucaOpsti'), life: 7000 });
      return;
    }
    if (!idDokument || tableData.length === 0) {
      toast.current.show({ severity: "warn", summary: "Sačuvajte dokument i dodajte stavke pre slanja.", life: 3000 });
      return;
    }
    setSefLoading(true);
    fetch(process.env.REACT_APP_BACKEND_URL + "/sef/SendKoKz", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
      body: JSON.stringify({ id_dokument: idDokument }),
    })
      .then(handleErrors)
      .then((data) => {
        setFormData((prev) => ({
          ...prev,
          statusSefa: SEF_POSLAT,
          sefStatusString: data.status,
        }));
        toast.current.show({
          severity: "success",
          summary: `Dokument poslat na SEF! Status: ${data.status}`,
          life: 5000,
        });
      })
      .catch((err) => {
        err.json?.().then((e) =>
          toast.current.show({ severity: "error", summary: "SEF greška", detail: e.message, life: 6000 })
        );
      })
      .finally(() => setSefLoading(false));
  };

  /* ── SEF: Otkaži / Storniraj ──
     Ista podela kao kod fakture: Cancel dok dokument nije zaživeo (Draft/New/
     SendingError), Storno kad jeste (Sent/Approved/Rejected). Do 11.09.2026 KO
     nije moglo ni da se pošalje, pa nije moglo ni pogrešno da se pošalje — sad
     može, a knjižno odobrenje nosi PDV posledicu i mora da se povuče. */
  /* Mistake = „greska prilikom slanja“ iz SEF dokumentacije. Ranije je ovde
     stajao nas interni marker "SendingError", koji SEF nikad ne vraca, pa se
     neuspelo poslat dokument nije mogao otkazati. */
  const SEF_CANCEL_STATUSI = ["Draft", "New", "Mistake", "SendingError"];
  const SEF_STORNO_STATUSI = ["Sent", "Approved", "Rejected"];

  const izvrsiSefPovlacenje = () => {
    const jeCancel = sefDialog.type === "cancel";
    setSefDialog((p) => ({ ...p, visible: false }));
    setSefLoading(true);
    fetch(process.env.REACT_APP_BACKEND_URL + "/sef/" + (jeCancel ? "CancelKoKz" : "StornoKoKz"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
      body: JSON.stringify({ id_dokument: idDokument, komentar: sefDialog.komentar }),
    })
      .then(handleErrors)
      .then((data) => {
        setFormData((prev) => ({
          ...prev,
          statusSefa: SEF_NIJE_POSLAT,
          sefStatusString: data.status,
        }));
        toast.current.show({
          severity: "success",
          summary: jeCancel ? "Dokument otkazan na SEF-u." : "Dokument storniran na SEF-u.",
          life: 4000,
        });
      })
      .catch((err) => {
        err.json?.().then((e) =>
          toast.current.show({ severity: "error", summary: "SEF greška", detail: e.message, life: 6000 })
        );
      })
      .finally(() => setSefLoading(false));
  };

  /* ── SEF: Proveri status ── */
  const handleSefStatus = () => {
    if (!idDokument) return;
    setSefLoading(true);
    fetch(process.env.REACT_APP_BACKEND_URL + "/sef/GetKoKzStatus", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + auth.token },
      body: JSON.stringify({ id_dokument: idDokument }),
    })
      .then(handleErrors)
      .then((data) => {
        setFormData((prev) => ({
          ...prev,
          sefStatusString: data.status,
          sefKomentar: data.komentar || prev.sefKomentar,
        }));
        toast.current.show({
          severity: "info",
          summary: `SEF status: ${data.status || "Nepoznat"}`,
          detail: data.komentar || "",
          life: 4000,
        });
      })
      .catch((err) => {
        err.json?.().then((e) =>
          toast.current.show({ severity: "error", summary: "Greška", detail: e.message, life: 4000 })
        );
      })
      .finally(() => setSefLoading(false));
  };

  /* ── Items DataTable header ── */
  const tableItemsHeader = (
    <div
      className="export-buttons fakture-stavke-header"
    >
      <Tooltip target=".export-buttons>button" position="bottom" />
      <div className="fakture-stavke-radnje">
        {!isLocked && idDokument && (
          <Button
            type="button"
            icon="pi pi-plus"
            severity="success"
            rounded
            onClick={openNewStavka}
            data-pr-tooltip="Nova stavka"
          />
        )}
        {selectedItemId && !isLocked && (
          <Button
            type="button"
            icon="pi pi-trash"
            severity="danger"
            rounded
            onClick={handleDeleteItem}
            data-pr-tooltip="Obriši stavku"
          />
        )}
      </div>
      <input
        type="search"
        placeholder={t('common.search')}
        className="p-inputtext p-component"
        onInput={(e) => setGlobalFilter(e.target.value)}
      />
    </div>
  );

  /* ── Render ── */
  return (
    <div className="col-12">
      <ConfirmDialog />
      <Toast ref={toast} />

      {/* Toolbar */}
      <div className="" style={{marginBottom:'1%'}}>
        <Button
          label="Prevuci stavke sa faktura"
          icon="pi pi-download"
          type="button"
          style={{ padding: "0.2rem 0.5rem", maxHeight: "30px" }}
          disabled={!prevuciEnabled || prevuciLoading || isLocked}
          onClick={handlePrevuciStavke}
          loading={prevuciLoading}
        />
        {isDinarska && sefKonfigurisan && (
          <Button
            label={sefLoading ? "Obrada..." : "Slanje e-faktura"}
            type="button"
            className="p-button-success"
            style={{ padding: "0.2rem 0.5rem", maxHeight: "30px" }}
            onClick={handleSendToSef}
            disabled={sefLoading || isLocked || !idDokument}
          />
        )}
        {isDinarska && sefKonfigurisan && isSefPoslat && (
          <Button
            label={sefLoading ? "Obrada..." : "Proveri SEF status"}
            type="button"
            className="p-button-secondary"
            style={{ padding: "0.2rem 0.5rem", maxHeight: "30px" }}
            onClick={handleSefStatus}
            disabled={sefLoading}
          />
        )}
        {idDokument && sefKonfigurisan && SEF_CANCEL_STATUSI.includes(formData.sefStatusString) && (
          <Button
            label={sefLoading ? "Obrada..." : "Otkaži SEF"}
            type="button"
            className="p-button-warning"
            style={{ padding: "0.2rem 0.5rem", maxHeight: "30px" }}
            onClick={() => setSefDialog({ visible: true, type: "cancel", komentar: "" })}
            disabled={sefLoading}
          />
        )}
        {idDokument && sefKonfigurisan && SEF_STORNO_STATUSI.includes(formData.sefStatusString) && (
          <Button
            label={sefLoading ? "Obrada..." : "Storniraj SEF"}
            type="button"
            className="p-button-danger"
            style={{ padding: "0.2rem 0.5rem", maxHeight: "30px" }}
            onClick={() => setSefDialog({ visible: true, type: "storno", komentar: "" })}
            disabled={sefLoading}
          />
        )}
        {idDokument && (
          <Button
            label="Storno"
            type="button"
            className="p-button-danger"
            style={{ padding: "0.2rem 0.5rem", maxHeight: "30px" }}
            onClick={handleDelete}
            disabled={isLocked}
          />
        )}
      </div>

      {/* SEF: otkazivanje / storniranje — komentar je obavezan na SEF-ovoj
          strani (CancelCommentNotDefined / StornoCommentNotDefined), pa se
          nikad ne šalje prazan. */}
      <Dialog
        visible={sefDialog.visible}
        onHide={() => setSefDialog((p) => ({ ...p, visible: false }))}
        header={sefDialog.type === "cancel" ? "Otkazivanje dokumenta na SEF-u" : "Storniranje dokumenta na SEF-u"}
        style={{ width: "420px" }}
        footer={
          <div>
            <Button label={t("nova.odustani")} icon="pi pi-times" className="p-button-text"
              onClick={() => setSefDialog((p) => ({ ...p, visible: false }))} />
            <Button
              label={sefDialog.type === "cancel" ? "Otkaži na SEF-u" : "Storniraj"}
              icon={sefDialog.type === "cancel" ? "pi pi-times-circle" : "pi pi-exclamation-triangle"}
              className="p-button-danger"
              onClick={izvrsiSefPovlacenje}
              disabled={sefLoading || !sefDialog.komentar.trim()}
            />
          </div>
        }
      >
        <p style={{ marginBottom: "1rem" }}>
          {sefDialog.type === "cancel"
            ? "Da li ste sigurni da želite da OTKAŽETE ovaj dokument na SEF-u?"
            : "Da li ste sigurni da želite da STORNIRATE ovaj dokument na SEF-u? Ova akcija je nepovratna."}
        </p>
        <label>
          {sefDialog.type === "cancel" ? t("nova.razlogOtkazivanja") : t("nova.komentarStorniranja")}:
        </label>
        <InputTextarea
          value={sefDialog.komentar}
          onChange={(e) => setSefDialog((p) => ({ ...p, komentar: e.target.value }))}
          rows={3}
          style={{ width: "100%", marginTop: "0.5rem" }}
          autoFocus
        />
      </Dialog>

      {/* Lock banner */}
      {isLocked && (
        <div
          className="col-12"
          style={{
            padding: "0.6rem 1rem",
            marginLeft: "1%",
            marginBottom: "1%",
            maxWidth: "fit-content",
            borderRadius: "4px",
            fontWeight: "bold",
            ...lockStyle,
          }}
        >
          {lockReason}
          {formData.sefStatusString && (
            <span style={{ marginLeft: "1rem", fontWeight: "normal" }}>
              SEF: {formData.sefStatusString}
            </span>
          )}
        </div>
      )}

      {/* Forma sa Accordion */}
      <form
        autoComplete="off"
        className="novaDinarskaIzlazneForm"
        style={isLocked ? { opacity: 0.85 } : undefined}
      >
        <Accordion multiple activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>

          {/* ─ OSNOVNI PODACI ─ */}
          <AccordionTab id="AccordionTabUnosZaposlenih" header="OSNOVNI PODACI">
            <div className="gridFakture">
              <div className="col-12 md:col-3" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.dokumentBr')}:</label>
                <InputText
                  value={formData.brojDokumenta || ""}
                  readOnly
                  placeholder="Auto"
                  style={{ backgroundColor: "#efefef" }}
                />
              </div>

              <div className="col-12 md:col-3" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.vrstaDokumenta')}: <span className="iz-obavezno">*</span></label>
                <Dropdown
                  name="TipDokumenta"
                  value={formData.TipDokumenta}
                  options={TIP_DOKUMENTA_OPTIONS}
                  onChange={handleChange}
                  placeholder="Izaberite tip"
                  disabled={isLocked || !!idDokument}
                  invalid={invalidCtrl(formData.TipDokumenta)}
                  style={{ width: "100%" }}
                />
              </div>

              <div className="col-12 md:col-6" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.klijent')}: <span className="iz-obavezno">*</span></label>
                <CustomDropdown
                  name="id_klijent"
                  value={formData.id_klijent}
                  options={klijentiOptions}
                  optionLabel="label"
                  onChange={handleChange}
                  placeholder="Izaberi klijenta"
                  disabled={isLocked}
                  invalid={invalidCtrl(formData.id_klijent)}
                  style={{ width: "100%" }}
                />
              </div>

              <div className="col-12 md:col-3" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.datum')}: <span className="iz-obavezno">*</span></label>
                <Calendar
                  name="datum"
                  value={formData.datum}
                  onChange={handleChange}
                  dateFormat="dd.mm.yy"
                  showIcon
                  mask="99.99.9999"
                  disabled={isLocked}
                  invalid={invalidCtrl(formData.datum)}
                />
              </div>

              <div className="col-12 md:col-3" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.datumPromUsluge')}:</label>
                <Calendar
                  name="dpu"
                  value={formData.dpu}
                  onChange={handleChange}
                  dateFormat="dd.mm.yy"
                  showIcon
                  mask="99.99.9999"
                  disabled={isLocked}
                />
              </div>

              <div className="col-12 md:col-3" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.rokPlacanjaDani')}:</label>
                <InputNumber
                  name="rokPlacanjaDani"
                  value={formData.rokPlacanjaDani}
                  onValueChange={handleChange}
                  min={0}
                  disabled={isLocked}
                  style={{ width: "100%" }}
                />
              </div>

              <div className="col-12 md:col-3" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.rokPlacanjaDatum')}:</label>
                <Calendar
                  name="rokPlacanjaDatum"
                  value={formData.rokPlacanjaDatum}
                  onChange={handleChange}
                  dateFormat="dd.mm.yy"
                  showIcon
                  mask="99.99.9999"
                  disabled={isLocked}
                />
              </div>

              <div className="col-12 md:col-4" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.tekuciRacun')}:</label>
                <Dropdown
                  name="tekuciRacun"
                  value={formData.tekuciRacun}
                  options={tekuciRacunOptions}
                  onChange={handleChange}
                  placeholder="Izaberi račun"
                  disabled={isLocked}
                  style={{ width: "100%" }}
                />
              </div>

              <div className="col-12" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.povezaneFakture')}:</label>
                <MultiSelect
                  value={selectedFakture}
                  options={faktureOptions}
                  onChange={(e) => {
                    setSelectedFakture(e.value);
                    setPrevuciEnabled(false);
                  }}
                  placeholder="Izaberite fakture..."
                  filter
                  showClear
                  disabled={isLocked}
                  style={{ width: "100%" }}
                  display="chip"
                />
              </div>
            </div>
          </AccordionTab>

          {/* ─ SPECIFIČNOSTI ─ */}
          <AccordionTab id="AccordionTabUnosZaposlenih" header="SPECIFIČNOSTI">
            <div className="gridFakture">
              <div className="col-12 md:col-3" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.vozilo')}:</label>
                <InputText
                  name="vozilo"
                  value={formData.vozilo || ""}
                  onChange={handleChange}
                  disabled={isLocked}
                />
              </div>
              <div className="col-12 md:col-3" style={{ display: "flex", flexDirection: "column" }}>
                <label>CMR:</label>
                <InputText
                  name="cmr"
                  value={formData.cmr || ""}
                  onChange={handleChange}
                  disabled={isLocked}
                />
              </div>
              <div className="col-12 md:col-3" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.nasaRef')}:</label>
                <InputText
                  name="refNasa"
                  value={formData.refNasa || ""}
                  onChange={handleChange}
                  disabled={isLocked}
                />
              </div>
              <div className="col-12 md:col-3" style={{ display: "flex", flexDirection: "column" }}>
                <label>Ref. No:</label>
                <InputText
                  name="refNo"
                  value={formData.refNo || ""}
                  onChange={handleChange}
                  disabled={isLocked}
                />
              </div>
            </div>
          </AccordionTab>

          {/* ─ PODACI VALUTE ─ */}
          <AccordionTab id="AccordionTabUnosZaposlenih" header="PODACI VALUTE">
            <div className="gridFakture">
              <div className="col-12 md:col-4" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.valuta')}: <span className="iz-obavezno">*</span></label>
                <CustomDropdown
                  name="valuta"
                  value={formData.valuta}
                  options={valutaOptions}
                  optionLabel="label"
                  onChange={handleChange}
                  placeholder={t('nova.phValutu')}
                  disabled={isLocked}
                  invalid={invalidCtrl(formData.valuta)}
                  style={{ width: "100%" }}
                />
              </div>
              {!isDinarska && (
                <div className="col-12 md:col-3" style={{ display: "flex", flexDirection: "column" }}>
                  <label>{t('nova.kurs')}:</label>
                  <InputNumber
                    name="kurs"
                    value={formData.kurs}
                    onValueChange={handleChange}
                    minFractionDigits={4}
                    maxFractionDigits={6}
                    disabled={isLocked}
                    style={{ width: "100%" }}
                  />
                </div>
              )}
              <div className="col-12 md:col-3" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.datumSlanjaPoste')}:</label>
                <Calendar
                  name="datumSlanjaPoste"
                  value={formData.datumSlanjaPoste}
                  onChange={handleChange}
                  dateFormat="dd.mm.yy"
                  showIcon
                  mask="99.99.9999"
                  disabled={isLocked}
                />
              </div>
              <div className="col-12 md:col-3" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.datumPrijemaPoste')}:</label>
                <Calendar
                  name="datumPrijemaPoste"
                  value={formData.datumPrijemaPoste}
                  onChange={handleChange}
                  dateFormat="dd.mm.yy"
                  showIcon
                  mask="99.99.9999"
                  disabled={isLocked}
                />
              </div>
            </div>
          </AccordionTab>

          {/* ─ NAPOMENE ─ */}
          <AccordionTab id="AccordionTabUnosZaposlenih" header={t('nova.napomene')}>
            <div className="gridFakture">
              <div className="col-12" style={{ flex: "0 0 39%", maxWidth: "50%", display: "flex", flexDirection: "column" }}>
                <label>{t('nova.napomenaPoreskoOslob')}:</label>
                <InputTextarea
                  name="napomenaPoreskoOslobadjanje"
                  value={formData.napomenaPoreskoOslobadjanje || ""}
                  onChange={handleChange}
                  disabled={isLocked}
                  rows={3}
                />
              </div>
              <div className="col-12" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.napomena')}:</label>
                <InputTextarea
                  name="napomena"
                  value={formData.napomena || ""}
                  onChange={handleChange}
                  disabled={isLocked}
                  rows={3}
                />
              </div>
              <div className="col-12" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.napomenaProvere')}:</label>
                <InputTextarea
                  name="napomenaProvera"
                  value={formData.napomenaProvera || ""}
                  onChange={handleChange}
                  disabled={isLocked}
                  rows={2}
                />
              </div>
            </div>
          </AccordionTab>

          {/* ─ STATUSI ─ */}
          <AccordionTab id="AccordionTabUnosZaposlenih" header="STATUSI">
            <div className="gridFakture">
              <div className="col-12 md:col-4" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.statusProvere')}:</label>
                <InputText
                  readOnly
                  value={
                    formData.statusProvere === null || formData.statusProvere === undefined
                      ? "Na čekanju"
                      : formData.statusProvere === true || formData.statusProvere === 1
                      ? "✓ Prihvaćen"
                      : "✗ Odbijen"
                  }
                  style={{
                    width: "100%",
                    background: "#f5f5f5",
                    color:
                      formData.statusProvere === null || formData.statusProvere === undefined
                        ? "#888"
                        : formData.statusProvere === true || formData.statusProvere === 1
                        ? "#2e7d32"
                        : "#c62828",
                    fontWeight: "bold",
                  }}
                />
              </div>
              <div className="col-12 md:col-8" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.proverioId')}:</label>
                <InputText
                  readOnly
                  value={formData.user_id_proverio || ""}
                  placeholder="—"
                  style={{ width: "100%", background: "#f5f5f5" }}
                />
              </div>
              <div className="col-12 md:col-6" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.statusSefaInterni')}:</label>
                <Dropdown
                  name="statusSefa"
                  value={formData.statusSefa}
                  options={statusSefaOptions}
                  onChange={handleChange}
                  placeholder="Izaberi status"
                  disabled={isLocked}
                  style={{ width: "100%" }}
                />
              </div>
              <div className="col-12 md:col-6" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.sefStatusSa')}:</label>
                <InputText
                  readOnly
                  value={formData.sefStatusString || ""}
                  placeholder="—"
                  style={{ width: "100%", background: "#f5f5f5" }}
                />
              </div>
              <div className="col-12 md:col-6" style={{ display: "flex", flexDirection: "column" }}>
                <label>{t('nova.sefKomentar')}:</label>
                <InputText
                  readOnly
                  value={formData.sefKomentar || ""}
                  placeholder="—"
                  style={{ width: "100%", background: "#f5f5f5" }}
                />
              </div>
            </div>
          </AccordionTab>

        </Accordion>
      </form>

      {/* Submit dugme */}
      <div className="formUnosZap buttonUnosZap buttonUnosFakture">
        <button
          type="button"
          onClick={handleSave}
          className="btn btn-success"
          disabled={isLocked}
        >
          {idDokument ? t("common.save") : t("admin.unos")}
        </button>
      </div>

      {/* ─ Dialog za unos/izmenu stavke ─ */}
      <Dialog
        visible={stavkaDialogVisible}
        onHide={closeStavkaDialog}
        header={selectedItemId ? "Izmena stavke" : "Nova stavka"}
        style={{ width: "700px" }}
        modal
        footer={
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            {selectedItemId && (
              <Button
                label="Obriši"
                icon="pi pi-trash"
                severity="danger"
                type="button"
                onClick={handleDeleteItem}
              />
            )}
            <Button
              label="Odustani"
              icon="pi pi-times"
              severity="secondary"
              type="button"
              onClick={closeStavkaDialog}
            />
            <Button
              label={selectedItemId ? "Sačuvaj" : "Dodaj stavku"}
              icon="pi pi-check"
              severity="success"
              type="button"
              onClick={handleSaveItem}
            />
          </div>
        }
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
          <div style={{ flex: "1 1 240px", display: "flex", flexDirection: "column" }}>
            <label>{t('nova.sifraStavke')}: <span className="iz-obavezno">*</span></label>
            <Dropdown
              name="sifraStavke"
              value={formDataItems.sifraStavke}
              options={sifraStavkeOptions}
              onChange={(e) => handleChangeItems({ target: { name: "sifraStavke", value: e.value } })}
              placeholder="Izaberite šifru"
              filter
              invalid={invalidCtrlItem(formDataItems.sifraStavke)}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ flex: "1 1 200px", display: "flex", flexDirection: "column" }}>
            <label>{t('nova.opis')}:</label>
            <InputTextarea
              name="tekst"
              value={formDataItems.tekst || ""}
              onChange={(e) => handleChangeItems({ target: { name: "tekst", value: e.target.value } })}
              rows={2}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ flex: "1 1 180px", display: "flex", flexDirection: "column" }}>
            <label>{t('nova.slobodanTekst')}:</label>
            <InputTextarea
              name="slobodanTekst"
              value={formDataItems.slobodanTekst || ""}
              onChange={(e) =>
                handleChangeItems({ target: { name: "slobodanTekst", value: e.target.value } })
              }
              rows={2}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ flex: "0 0 140px", display: "flex", flexDirection: "column" }}>
            <label>{t('nova.jedinicnaCena')}:</label>
            <InputNumber
              name="jedinicnaCena"
              value={formDataItems.jedinicnaCena}
              onValueChange={(e) =>
                handleChangeItems({ target: { name: "jedinicnaCena", value: e.value } })
              }
              minFractionDigits={2}
              maxFractionDigits={2}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ flex: "0 0 120px", display: "flex", flexDirection: "column" }}>
            <label>{t('nova.kolicina')}:</label>
            <InputNumber
              name="kolicina"
              value={formDataItems.kolicina}
              onValueChange={(e) =>
                handleChangeItems({ target: { name: "kolicina", value: e.value } })
              }
              minFractionDigits={2}
              maxFractionDigits={2}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ flex: "0 0 140px", display: "flex", flexDirection: "column" }}>
            <label>{t('nova.neto')}: <span className="iz-obavezno">*</span></label>
            <InputNumber
              name="neto"
              value={formDataItems.neto}
              onValueChange={(e) => {
                const val = e.value;
                const pdvStopa = formDataItems.PDVstopa || 0;
                const pdvIznos = val != null ? (val * pdvStopa) / 100 : null;
                setFormDataItems((prev) => ({
                  ...prev,
                  neto: val,
                  PDVIznos: pdvIznos,
                  bruto: val != null && pdvIznos != null ? val + pdvIznos : null,
                }));
              }}
              minFractionDigits={2}
              maxFractionDigits={2}
              invalid={invalidCtrlItem(formDataItems.neto)}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ flex: "0 0 100px", display: "flex", flexDirection: "column" }}>
            <label>{t('nova.pdvStopa')}:</label>
            <InputNumber
              name="PDVstopa"
              value={formDataItems.PDVstopa}
              onValueChange={(e) =>
                handleChangeItems({ target: { name: "PDVstopa", value: e.value } })
              }
              minFractionDigits={0}
              maxFractionDigits={2}
              min={0}
              max={100}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ flex: "0 0 130px", display: "flex", flexDirection: "column" }}>
            <label>{t('nova.pdvIznos')}:</label>
            <InputNumber
              name="PDVIznos"
              value={formDataItems.PDVIznos}
              onValueChange={(e) => {
                const val = e.value;
                setFormDataItems((prev) => ({
                  ...prev,
                  PDVIznos: val,
                  bruto: (prev.neto || 0) + (val || 0),
                }));
              }}
              minFractionDigits={2}
              maxFractionDigits={2}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ flex: "0 0 130px", display: "flex", flexDirection: "column" }}>
            <label>{t('nova.bruto')}:</label>
            <InputNumber
              name="bruto"
              value={formDataItems.bruto}
              onValueChange={(e) =>
                handleChangeItems({ target: { name: "bruto", value: e.value } })
              }
              minFractionDigits={2}
              maxFractionDigits={2}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ flex: "0 0 110px", display: "flex", flexDirection: "column" }}>
            <label>{t('nova.rabatProc')}:</label>
            <InputNumber
              name="rabatProc"
              value={formDataItems.rabatProc}
              onValueChange={(e) =>
                handleChangeItems({ target: { name: "rabatProc", value: e.value } })
              }
              minFractionDigits={2}
              maxFractionDigits={2}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ flex: "0 0 140px", display: "flex", flexDirection: "column" }}>
            <label>{t('nova.rabatIznos')}:</label>
            <InputNumber
              name="rabat"
              value={formDataItems.rabat}
              onValueChange={(e) =>
                handleChangeItems({ target: { name: "rabat", value: e.value } })
              }
              minFractionDigits={2}
              maxFractionDigits={2}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ flex: "0 0 170px", display: "flex", flexDirection: "column" }}>
            <label>{t('nova.jedinicaMere')}:</label>
            <Dropdown
              name="jedinicaMere"
              value={formDataItems.jedinicaMere}
              options={jedinicaMereOptions}
              onChange={(e) =>
                handleChangeItems({ target: { name: "jedinicaMere", value: e.value } })
              }
              placeholder="JM"
              style={{ width: "100%" }}
            />
          </div>
        </div>
      </Dialog>

      {/* Tabela stavki */}
      <div
        className="tableItems"
        style={{ maxHeight: "60vh", marginTop: "1%",marginBottom: "0 !important", ...(isLocked ? { opacity: 0.85 } : {}) }}
      >
        <Tooltip target=".export-buttons>button" position="bottom" />
        <DataTable
          value={tableData}
          paginator
          rows={5}
          globalFilter={globalFilter}
          scrollable
          scrollHeight="35vh"
          header={tableItemsHeader}
          resizableColumns
          columnResizeMode="expand"
          className="izlazneFaktureTable stavke-tabela"
          rowClassName={(r) =>
            String(r.id) === String(selectedItemId) ? { "selected-row": true } : {}
          }
        >
          <Column
            header={t('nova.izaberi')}
            style={{ minWidth: 85 }}
            body={(rowData) => (
              <Button
                label={t('nova.izaberi')}
                icon="pi pi-eye"
                type="button"
                className="p-button-text p-button-sm"
                onClick={() => handleRowSelect(rowData)}
              />
            )}
          />
          <Column field="tekst" header="Opis" style={{ minWidth: 160 }} />
          <Column field="slobodanTekst" header="Sl. tekst" style={{ minWidth: 120 }} />
          <Column field="kolicina" header={t('nova.kolicina')} style={{ minWidth: 65 }} />
          <Column
            field="neto"
            header={t('nova.neto')}
            style={{ minWidth: 100 }}
            body={(r) => r.neto?.toFixed(2)}
          />
          <Column field="PDVstopa" header="PDV%" style={{ minWidth: 65 }} />
          <Column
            field="PDVIznos"
            header="PDV iznos"
            style={{ minWidth: 95 }}
            body={(r) => r.PDVIznos?.toFixed(2)}
          />
          <Column
            field="bruto"
            header={t('nova.bruto')}
            style={{ minWidth: 100 }}
            body={(r) => r.bruto?.toFixed(2)}
          />
        </DataTable>
      </div>
    </div>
  );
};

export default NovaDokumentKOKZ;
